import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';

/**
 * PostHog boolean flag that gates the "Agents Perf" tab
 * (`/organizations/performance`) on top of the offer-coach payload flag.
 *
 * The tab reads offer accept rates from ClickHouse, and the offers it counts
 * (`offers.normalized_offers_raw`) have been written without an
 * `organization_id` or `created_at` since 2026-01-13, so every org sees
 * near-empty numbers. This flag hides the tab until that data is repaired.
 *
 * Absent or off in PostHog hides the tab in production; non-production builds
 * always show it. Re-enable it in PostHog (for example, for Indigo staff only)
 * once the offers sync is fixed and backfilled.
 */
export const AGENT_PERFORMANCE_TAB_FLAG = 'agent-performance-tab';

/**
 * `true` when the flag is on, `false` once PostHog has loaded flags and this
 * one is off or missing, and `undefined` while flags are still loading.
 *
 * PostHog's `isFeatureEnabled` returns `undefined` both before flags load and
 * for a flag that does not exist, so a missing flag would otherwise look like
 * "still loading" forever. Tracking the load separately lets an absent flag
 * resolve to `false`, which is what makes this kill switch default-off.
 */
export const useIsAgentPerformanceTabEnabled = (): boolean | undefined => {
  const posthog = usePostHog();
  const isEnabled = useFeatureFlagEnabled(AGENT_PERFORMANCE_TAB_FLAG);
  const [hasLoadedFlags, setHasLoadedFlags] = useState(
    () => posthog?.featureFlags?.hasLoadedFlags ?? false
  );

  useEffect(
    () => posthog?.onFeatureFlags(() => setHasLoadedFlags(true)),
    [posthog]
  );

  if (process.env.NODE_ENV !== 'production') {
    return true;
  }

  if (isEnabled) {
    return true;
  }

  return hasLoadedFlags ? false : undefined;
};
